import React, { useState, useEffect } from 'react';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import Swal from 'sweetalert2';
import Loading from '../Common/Loading';
import { useNavigate } from 'react-router-dom'; 

// 상위(RecordDate.tsx)에서 넘겨줄 Competition 타입
type Competition = {
  comp_date: string;
  comp_code: number;
  comp_title: string;
  comp_div_ls: string[];
  comp_place: string;
  comp_link: string;
};

// RecordList가 받을 props 구조
type RecordListProps = {
  dates: Competition[];
};

const RecordList: React.FC<RecordListProps> = ({ dates }) => {
  const [currentPage, setCurrentPage] = useState<number>(1);
  // 모달 열림 여부
  const [isOpenRecord, setIsOpenRecord] = useState<boolean>(false);
  // 입력 필드: 참가번호와 참가자 이름
  const [bibNum, setBibNum] = useState<string>('');     
  const [name, setName] = useState<string>('');
  // comp_title, comp_code는 상위 대회정보에서 전달됨
  const [compTitle, setCompTitle] = useState<string>('');  
  const [compCode, setCompCode] = useState<number>(1000);
  // 로딩 여부
  const [loading, setLoading] = useState<boolean>(false);
  // 다중 검색 결과를 저장할 상태
  const [recordOptions, setRecordOptions] = useState<any[]>([]);
  const [isSelectingRecord, setIsSelectingRecord] = useState<boolean>(false);

  const navigate = useNavigate();

  // "기록 조회" 버튼 → API 호출
  const recordCheck = () => {
    setLoading(true);
    axios.post('/api/record-info', { 
      comp_title: compTitle,
      bibNum: bibNum,
      name: name
    })
      .then((res) => {
        setLoading(false);
        const results = res.data;
        // console.log("recordCheck() 응답:", results);
        if (!results || results.length === 0) {
          closeRecordDialog();
          Swal.fire({
            icon: 'warning',
            title: '기록이 존재하지 않습니다.',
          });
        } else if (results.length === 1) {
          // 단일 결과면 바로 RecordInfo 페이지로 이동
          const selectedRecord = results[0];
          navigate(`/recordinfo/${compTitle}/${selectedRecord.Bib}`, {
            state: {
              data: res.data,
              compCode: compCode,
            }
          });
          console.log("단일응답:", res.data);
          closeRecordDialog();
        } else if (results.length >= 2){
          // 여러 결과가 있을 경우 선택 모달을 띄움
          setRecordOptions(results);
          setIsSelectingRecord(true);
        }
      })
      .catch((err) => {
        setLoading(false);
        closeRecordDialog();
        Swal.fire({
          icon: 'warning',
          title: '기록이 존재하지 않습니다.',
        });
        console.error(err);
      });
  };

  // 모달 열기/닫기
  const openRecordDialog = (title: string, code: number) => () => {
    setCompTitle(title);
    setCompCode(code);
    // 입력 필드 초기화
    setBibNum('');
    setName('');
    setIsOpenRecord(true);
  };

  const closeRecordDialog = () => {
    setIsOpenRecord(false);
  };

  // 페이지네이션 로직
  const itemsPerPage = 8;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = dates.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  useEffect(() => {
    setCurrentPage(1);
  }, [dates]);

  return (
    // <div className='w-[90rem]'>
    <div className="w-full max-w-[90rem] mx-auto">
      {/* 테이블 헤더 */}
      <div className='flex justify-evenly mt-10 mb-8'>
        <div className="flex items-center justify-center flex-1 text-center">대회 날짜</div>
        <div className="flex items-center justify-center flex-1 text-center">대회명</div>
        <div className="flex items-center justify-center flex-1 text-center">대회 장소</div>
        <div className="flex items-center justify-center flex-1 text-center">대회 구분</div>
        <div className="flex items-center justify-center flex-1 text-center">기록 조회</div>
      </div>
      <hr className="border-b-2 border-gray-500 mb-4" />

      {/* 대회 목록 표시 */}
      {currentItems.map((competition) => (
        <div key={competition.comp_code}>
          <div className="flex justify-evenly mt-4 mb-4">
            <div className="flex items-center justify-center flex-1 text-center">
              {competition.comp_date}
            </div>
            <div className="flex items-center justify-center flex-1 text-center">
              {competition.comp_title}
            </div>
            <div className="flex items-center justify-center flex-1 text-center">
              {competition.comp_place}
            </div>
            <div className="flex items-center justify-center flex-1 text-center">
              {competition.comp_div_ls.join(', ')}
            </div>
            <div className="flex items-center justify-center flex-1 text-center">
              <button
                onClick={openRecordDialog(competition.comp_title, competition.comp_code)}
                className="select-none rounded-lg bg-gradient-to-tr from-gray-900 to-gray-800 py-3 px-6 text-center align-middle font-sans text-xs font-bold uppercase text-white shadow-md transition-all hover:shadow-lg active:opacity-[0.85]"
              >
                기록 조회하기
              </button>
            </div>
          </div>
          <hr />
        </div>
      ))}
      <hr className="border-b-2 border-gray-500 mb-4" />

      {/* 페이지네이션 버튼 */}
      <div className="flex justify-center mt-10 mb-20">
        {Array.from({ length: Math.ceil(dates.length / itemsPerPage) }, (_, index) => (
          <button
            key={index}
            className={`mx-1 px-3 py-1 rounded-lg border ${currentPage === index + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
            onClick={() => paginate(index + 1)}
          >
            {index + 1}
          </button>
        ))}
      </div>

      {/* 기록 조회 모달 */}
      {isOpenRecord && (
        <div className="fixed inset-0 z-[990] grid h-screen w-screen place-items-center bg-black bg-opacity-60 backdrop-blur-sm">
          {/* <div className="w-[500px] rounded-lg bg-white p-4"> */}
          <div className="w-full max-w-md rounded-lg bg-white p-4">
            <div className="flex items-center justify-center font-sans text-2xl font-semibold text-blue-gray-900 mb-4">
              참가번호 또는 참가자 이름을 입력해주세요
            </div>
            <hr className="border-gray-500 mb-4" />
            <div className='w-auto flex flex-col justify-center items-center gap-3'>
              <TextField
                id="bib-input"
                label="참가 번호"
                variant="outlined"
                type='text'
                onChange={(e) => { setBibNum(e.target.value); }}
              />
              <TextField
                id="name-input"
                label="참가자 이름"
                variant="outlined"
                onChange={(e) => { setName(e.target.value); }}
              />
            </div>
            <div className="flex flex-wrap items-center justify-end mt-4">
              <button
                onClick={closeRecordDialog}
                className="px-6 py-3 mr-1 font-sans text-xs font-bold text-red-500 uppercase rounded-lg hover:bg-red-500/10"
              >
                취소
              </button>
              <button
                onClick={recordCheck}
                className="rounded-lg bg-gradient-to-tr from-green-600 to-green-400 py-3 px-6 font-sans text-xs font-bold uppercase text-white shadow-md transition-all hover:shadow-lg"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 다중 결과 선택 모달 */}
      {isSelectingRecord && (
        <div className="fixed inset-0 z-[995] grid h-screen w-screen place-items-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="w-[500px] rounded-lg bg-white p-4">
            <div className="mb-4 text-xl font-bold text-center">
              여러 결과가 있습니다. 선택해주세요.
            </div>
            <ul>
              {recordOptions.map((record, index) => (
                <li key={index} className="mb-2">
                  <button
                    className="w-full px-4 py-2 bg-blue-500 text-white rounded"
                    onClick={() => {
                      navigate(`/recordinfo/${compTitle}/${record.Bib}`, {
                        state: {
                          data: [record],
                          compCode: compCode,
                        }
                      });
                      console.log("recordCheck() 응답:", record);
                      setIsSelectingRecord(false);
                      closeRecordDialog();
                    }}
                  >
                    {record.Name} (참가번호: {record.Bib}) - (종목: {record.Division}) - (기록: {record.Time})
                  </button>
                </li>
              ))}
            </ul>
            <button
              className="mt-4 w-full px-4 py-2 bg-gray-300 text-black rounded"
              onClick={() => setIsSelectingRecord(false)}
            >
              취소
            </button>
          </div>
        </div>
      )}

      {/* 로딩 스피너 */}
      {loading && (
        <div className="fixed inset-0 z-[999] flex h-screen w-screen items-center justify-center bg-black bg-opacity-40">
          <div className="text-white">Loading...</div>
        </div>
      )}
    </div>
  );
};

export default RecordList;
